// Factor lists per pillar — the data table that drives weighted z-score blending.
// Two flavors: nonFinancial (used by standard, energy, utility, REIT peer groups)
// and financial (used only by banks/insurers/asset managers).
//
// The shape is enforced via PillarMapping from factor-config.ts so weights become
// optional everywhere (a factor without an explicit weight gets weight = 1 in
// scoreGroup's weightedAvgZ). Extracted from compute.ts on 2026-04-25.

import type { StockFactors } from './types';
import type { PillarMapping } from './factor-config';

export const PILLAR_FACTORS: { nonFinancial: PillarMapping; financial: PillarMapping; reit: PillarMapping } = {
  nonFinancial: {
    profitability: [
      // Forward (weight 2) — ROE/ROA added 2026-04-22, net margin added 2026-04-23
      { key: 'fwdRoe' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'fwdRoa' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'fwdNetMargin' as keyof StockFactors, invert: false, weight: 2 },
      // Trailing (weight 1)
      { key: 'roic' as keyof StockFactors, invert: false },
      { key: 'grossProfitToAssets' as keyof StockFactors, invert: false },
      { key: 'accrualsRatio' as keyof StockFactors, invert: true },  // lower accruals = better earnings quality
      // Dropped: ROA (r=0.88 w/ ROIC), CFO/Assets (r=0.82 w/ ROA, r=0.71 w/ ROIC), fwdOperMargin (r=0.94 w/ fwdNetMargin), roe (r=0.86 w/ roic — kept for financial peer group where roic is n/a)
    ],
    growth: [
      // Pure structural growth — `epsRevision` + `earningsSurprise` live in the
      // momentum (signal) pillar. fwdEpsCagr3yr mirrors Valuation's fwdPE3yr;
      // fwdOperLeverage = fwdEpsCagr2yr − fwdRevCagr2yr is a margin-expansion signal.
      // Forward (4)
      { key: 'fwdEpsCagr2yr' as keyof StockFactors, invert: false },
      { key: 'fwdRevCagr2yr' as keyof StockFactors, invert: false },
      { key: 'fwdEpsCagr3yr' as keyof StockFactors, invert: false },
      { key: 'nextQtrEpsGrowth' as keyof StockFactors, invert: false },
      // Derived forward (1) — margin trajectory
      { key: 'fwdOperLeverage' as keyof StockFactors, invert: false },
      // Trailing (1) — cash-based reality check
      { key: 'fcfGrowth3yr' as keyof StockFactors, invert: false },
      // Innovation investment proxy. Positive signal — captures the
      // compounder thesis (high-R&D businesses tend to compound). Null
      // for non-R&D businesses (banks, REITs, utilities, energy, retail).
      { key: 'rdIntensity' as keyof StockFactors, invert: false },
    ],
    safety: [
      // Forward (weight 2). Pushes Safety from 0% forward → ~30% forward at pillar level.
      { key: 'fwdDebtToEbitda' as keyof StockFactors, invert: true, weight: 2 },
      // Trailing (weight 1) — institutional rebuild:
      // Per 11-year IC research (data/factor-research.json), kept only factors with
      // t-stat ≥ 1.5 OR established academic standing. Dropped:
      //   - piotroski   (t=-0.09, pure noise on 763-stock survivor universe)
      //   - assetGrowth (t=-0.78 even when inverted, anti-predictive in 2015-2025)
      { key: 'currentRatio' as keyof StockFactors, invert: false },     // t=+2.13, IR=+0.71 (best Safety factor)
      { key: 'debtToEbitda' as keyof StockFactors, invert: true },      // t=+1.59, IR=+0.53
      { key: 'beta' as keyof StockFactors, invert: true },              // not in factor research; kept for academic risk-management role
      // Universal Safety additions (2026-05-14)
      { key: 'cdsSpread' as keyof StockFactors, invert: true },         // market-implied default risk (lower bps = safer)
      { key: 'shortInterestDaysToCover' as keyof StockFactors, invert: true }, // crowded short positioning (higher days = more risk)
    ],
    shareholderReturns: [
      // Forward (weight 2) — drives the forward Quality rebalance.
      { key: 'fwdDividendYield' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'impliedBuybackYield' as keyof StockFactors, invert: false, weight: 2 },
      // Trailing. buybackYield carries weight 1 (not 2) per factor research:
      // 11yr peer-group IC gave t=-0.23, IR=-0.08 — no longer earns the double weight.
      { key: 'buybackYield' as keyof StockFactors, invert: false, weight: 1 },
      { key: 'netDebtPaydownYield' as keyof StockFactors, invert: false, weight: 1 },
      { key: 'insiderNetBuy' as keyof StockFactors, invert: false, weight: 1 },             // smart money signal
      // Stock-based comp dilution drag — buybacks net of SBC determines real
      // shareholder yield over time. Invert: lower SBC/revenue = less
      // dilution. Universal, but only meaningful where SBC is issued at
      // scale (tech, biotech, broker-dealers).
      { key: 'sbcDilution' as keyof StockFactors, invert: true, weight: 1 },
    ],
    valuation: [
      // All weights = 1. Forward/long-horizon emphasis comes from HAVING three
      // forward P/Es (1yr, 2yr, 3yr), not from horizon weighting — symmetric
      // with Growth which uses no year-3 forward factors.
      // GARP/compounder philosophy: paying up today is OK if justified 3 years
      // out, but the 3yr P/E doesn't need triple weighting when we already have
      // three forward P/Es in play.
      { key: 'fwdPE' as keyof StockFactors, invert: true },
      { key: 'fwdPE2yr' as keyof StockFactors, invert: true },
      { key: 'fwdPE3yr' as keyof StockFactors, invert: true }, // BBG BEST_EPS_3BF
      { key: 'fwdEvToEbitda' as keyof StockFactors, invert: true },
      { key: 'pegRatio' as keyof StockFactors, invert: true },
      { key: 'evToEbitda' as keyof StockFactors, invert: true }, // trailing TEV/EBITDA floor
    ],
    momentum: [
      // Z-axis "Momentum" composite — market-sentiment / momentum factors.
      // Display-only: does NOT influence Alpha Zone selection (that stays 2D Q, V).
      // Measures: "is the market confirming or fighting the Q+V thesis?"
      // `nearHigh` was dropped: IC audit on 9yr cache gave t-stat 0.75 and
      // L/S decile spread was NEGATIVE (-12.2%). George-Hwang 2004 doesn't
      // replicate on this 763-stock universe.
      { key: 'momentum' as keyof StockFactors, invert: false },           // 12-1 momentum (Jegadeesh-Titman)
      // shortTermReversal not inverted — treated as a simple 1-month momentum
      // signal. Lost the academic mean-reversion read; gained display
      // consistency (high return = green = positive).
      { key: 'shortTermReversal' as keyof StockFactors, invert: false },
      { key: 'epsRevision' as keyof StockFactors, invert: false },        // analyst upgrades
      { key: 'earningsSurprise' as keyof StockFactors, invert: false },   // post-earnings drift
    ],
  },
  financial: {
    profitability: [
      { key: 'fwdRoe' as keyof StockFactors, invert: false, weight: 2 },
      // Forward net margin works for banks (NI/Sales). Operating margin dropped — r=0.94 with net margin.
      { key: 'fwdNetMargin' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'roe' as keyof StockFactors, invert: false, weight: 1 },  // kept for financials (no roic here)
      // ROTCE (NI / tangible common equity) is the canonical bank-profitability
      // metric — strips intangibles + goodwill out of the denominator so
      // acquisitive banks don't look more efficient than they are. Sourced
      // BBG-direct via ARDR_TANG_COMMON_EQUITY.
      { key: 'rotce' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'nim' as keyof StockFactors, invert: false, weight: 1 },
      { key: 'efficiencyRatio' as keyof StockFactors, invert: true, weight: 1 },
      // Replaces accrualsRatio for banks: CFO isn't meaningful here. Provisions/NII
      // is the bank equivalent of earnings quality — lower = higher-quality earnings
      // (fewer credit losses per dollar of net interest income).
      { key: 'provisionsToNII' as keyof StockFactors, invert: true, weight: 1 },
    ],
    growth: [
      // Banks Growth mirrors non-financial — same forward CAGR ladder + fwdOperLeverage.
      { key: 'fwdEpsCagr2yr' as keyof StockFactors, invert: false },
      { key: 'fwdRevCagr2yr' as keyof StockFactors, invert: false },
      { key: 'fwdEpsCagr3yr' as keyof StockFactors, invert: false },
      { key: 'nextQtrEpsGrowth' as keyof StockFactors, invert: false },
      { key: 'fwdOperLeverage' as keyof StockFactors, invert: false },
      // Trailing (1) — banks use revenue3yr since they lack meaningful FCF
      { key: 'revenueGrowth3yr' as keyof StockFactors, invert: false },
    ],
    safety: [
      // Bank-specific safety rebuilt 2026-05-14. Old setup was beta + equityToAssets
      // only (2 factors, far thinner than the non-financial Safety pillar). Banks
      // have three families of risk that the GAAP-style equity/assets ratio doesn't
      // capture: regulatory capital adequacy, credit quality, reserve coverage.
      { key: 'beta' as keyof StockFactors, invert: true },
      // Regulatory capital adequacy — tier-1 capital / RWA. The metric regulators
      // actually use (equity/assets below is a naive cousin). Weight 1 not 2:
      // BBG only returns tier1 on 54/126 financial-peer names (mostly US listings),
      // so a 2x weight would over-discriminate the 43% with full coverage and
      // tilt the pillar toward US large banks. Bump back to 2 once BBG coverage
      // on Canadian / foreign banks improves.
      { key: 'tier1CapRatio' as keyof StockFactors, invert: false, weight: 1 },
      // Credit quality: non-performing loans as % of total loans. Inverted —
      // lower NPL = safer book.
      { key: 'nplRatio' as keyof StockFactors, invert: true },
      // Reserve coverage: allowance for loan losses / non-performing assets.
      // Higher = more cushion against credit losses already on the balance sheet.
      { key: 'provCoverage' as keyof StockFactors, invert: false },
      // Retained as legacy fallback for banks where BBG tier1CapRatio is null
      // (mostly non-US listings). Lower weight reflects the proper version above.
      { key: 'equityToAssets' as keyof StockFactors, invert: false },
      // Universal Safety additions (2026-05-14)
      { key: 'cdsSpread' as keyof StockFactors, invert: true },
      { key: 'shortInterestDaysToCover' as keyof StockFactors, invert: true },
    ],
    shareholderReturns: [
      // `netDebtPaydownYield` is excluded for banks. The factor is
      // -ΔTotalDebt/marketCap — a clean capital-discipline signal for non-fins,
      // but for banks "total debt" includes deposits, repos, and short-term funding
      // — operational liabilities, not capital allocation. Deposits grew across the
      // universe in 2025 → every major bank scored deeply negative on this factor
      // (JPM -1.01, BAC -1.15, C -2.51, HSBC -0.89). It was systematically grading
      // banks down for being banks, dragging JPM Q to -1.02 against street consensus.
      // Replaced with the forward yields used by non-financials (fwdDividendYield,
      // impliedBuybackYield) which apply cleanly to bank capital returns. Trailing
      // dividendYield still excluded — yield-trap dynamic (t=-2.05, IR=-0.68 in
      // factor research) applies to banks too.
      { key: 'fwdDividendYield' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'impliedBuybackYield' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'buybackYield' as keyof StockFactors, invert: false, weight: 1 },
      { key: 'insiderNetBuy' as keyof StockFactors, invert: false, weight: 1 },
    ],
    valuation: [
      // Banks keep book-value factors. All weights = 1 — consistent with non-financial pillar.
      { key: 'fwdPE' as keyof StockFactors, invert: true },
      { key: 'fwdPE2yr' as keyof StockFactors, invert: true },
      { key: 'fwdPE3yr' as keyof StockFactors, invert: true },
      { key: 'fwdPtoBook' as keyof StockFactors, invert: true }, // BBG forward book/share
      { key: 'pToRevenue' as keyof StockFactors, invert: true }, // price/NII for banks
      { key: 'priceToBook' as keyof StockFactors, invert: true },
    ],
    momentum: [
      // Same 4 momentum factors as non-financial (see non-fin note for the nearHigh drop).
      { key: 'momentum' as keyof StockFactors, invert: false },
      { key: 'shortTermReversal' as keyof StockFactors, invert: false },
      { key: 'epsRevision' as keyof StockFactors, invert: false },
      { key: 'earningsSurprise' as keyof StockFactors, invert: false },
    ],
  },
  // REIT-specific flavor. REITs distribute ~90% of taxable income, so GAAP
  // net income / EPS / FCF understate the cash they generate. Property
  // metrics (FFO, AFFO, NAV, occupancy, same-store NOI growth) are the
  // right lens. Scoring REITs on generic P/E + ROIC penalizes them for
  // being REITs.
  reit: {
    profitability: [
      { key: 'fwdRoe' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'fwdNetMargin' as keyof StockFactors, invert: false, weight: 1 },
      { key: 'roe' as keyof StockFactors, invert: false, weight: 1 },
      // REIT-specific
      { key: 'ffoYield' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'occupancyRate' as keyof StockFactors, invert: false, weight: 1 },
      { key: 'sameStoreNoiGrowth' as keyof StockFactors, invert: false, weight: 1 },
    ],
    growth: [
      // Same forward CAGR ladder; trailing tail uses sameStoreNoiGrowth instead
      // of fcfGrowth3yr (REIT FCF is messy with property sales).
      { key: 'fwdEpsCagr2yr' as keyof StockFactors, invert: false },
      { key: 'fwdRevCagr2yr' as keyof StockFactors, invert: false },
      { key: 'fwdEpsCagr3yr' as keyof StockFactors, invert: false },
      { key: 'nextQtrEpsGrowth' as keyof StockFactors, invert: false },
      { key: 'fwdOperLeverage' as keyof StockFactors, invert: false },
      { key: 'sameStoreNoiGrowth' as keyof StockFactors, invert: false },
    ],
    safety: [
      // Forward debt/EBITDA still meaningful for REITs (they're levered).
      // currentRatio drops — REITs don't run a working-capital cycle.
      // Adds reAvgLeaseTerm — longer-dated leases reduce roll-off risk.
      { key: 'fwdDebtToEbitda' as keyof StockFactors, invert: true, weight: 2 },
      { key: 'debtToEbitda' as keyof StockFactors, invert: true, weight: 1 },
      { key: 'beta' as keyof StockFactors, invert: true, weight: 1 },
      { key: 'reAvgLeaseTerm' as keyof StockFactors, invert: false, weight: 1 },
      // Universal Safety additions (2026-05-14)
      { key: 'cdsSpread' as keyof StockFactors, invert: true, weight: 1 },
      { key: 'shortInterestDaysToCover' as keyof StockFactors, invert: true, weight: 1 },
    ],
    shareholderReturns: [
      // REITs are dividend-led; trailing dividendYield matters more than for
      // non-financials (the 90% distribution rule means the yield IS the
      // story). Buyback yield kept but less central.
      { key: 'fwdDividendYield' as keyof StockFactors, invert: false, weight: 2 },
      { key: 'dividendYield' as keyof StockFactors, invert: false, weight: 1 },
      { key: 'impliedBuybackYield' as keyof StockFactors, invert: false, weight: 1 },
      { key: 'buybackYield' as keyof StockFactors, invert: false, weight: 1 },
      { key: 'insiderNetBuy' as keyof StockFactors, invert: false, weight: 1 },
    ],
    valuation: [
      // REIT-canonical: P/FFO + P/NAV are primary. fwdPE still useful as the
      // consensus's forward read. EV/EBITDA capital-structure agnostic, kept.
      // Dropped fwdPE3yr + pegRatio + pToFcf + fwdEvToRevenue — REIT GAAP
      // earnings can't sustain 3-year forward P/E or PEG math, and FCF is
      // distorted by property sales.
      { key: 'pToFfo' as keyof StockFactors, invert: true, weight: 2 },
      { key: 'pToNav' as keyof StockFactors, invert: true, weight: 2 },
      { key: 'fwdPE' as keyof StockFactors, invert: true, weight: 1 },
      { key: 'fwdPE2yr' as keyof StockFactors, invert: true, weight: 1 },
      { key: 'fwdEvToEbitda' as keyof StockFactors, invert: true, weight: 1 },
      { key: 'evToEbitda' as keyof StockFactors, invert: true, weight: 1 },
      { key: 'priceToBook' as keyof StockFactors, invert: true, weight: 1 },
    ],
    momentum: [
      { key: 'momentum' as keyof StockFactors, invert: false },
      { key: 'shortTermReversal' as keyof StockFactors, invert: false },
      { key: 'epsRevision' as keyof StockFactors, invert: false },
      { key: 'earningsSurprise' as keyof StockFactors, invert: false },
    ],
  },
};

/**
 * Union of factor entries per pillar across both flavors. Used by the
 * Factor Breakdown popup to render ALL factors (not just the ones in the
 * current stock's flavor), greying out the ones that don't apply.
 * Steve directive 2026-05-14: "for each stock, so we know what we are
 * looking at" — show the unused factors dimmed instead of hiding them.
 */
export function unionPillarMapping(): { profitability: typeof PILLAR_FACTORS.nonFinancial.profitability; growth: typeof PILLAR_FACTORS.nonFinancial.growth; safety: typeof PILLAR_FACTORS.nonFinancial.safety; shareholderReturns: typeof PILLAR_FACTORS.nonFinancial.shareholderReturns; valuation: typeof PILLAR_FACTORS.nonFinancial.valuation; momentum: typeof PILLAR_FACTORS.nonFinancial.momentum } {
  const pillars = ['profitability', 'growth', 'safety', 'shareholderReturns', 'valuation', 'momentum'] as const;
  const out: Partial<Record<typeof pillars[number], unknown>> = {};
  for (const p of pillars) {
    const seen = new Set<string>();
    const merged: typeof PILLAR_FACTORS.nonFinancial.profitability = [];
    for (const f of PILLAR_FACTORS.nonFinancial[p]) {
      if (!seen.has(String(f.key))) { seen.add(String(f.key)); merged.push(f); }
    }
    for (const f of PILLAR_FACTORS.financial[p]) {
      if (!seen.has(String(f.key))) { seen.add(String(f.key)); merged.push(f); }
    }
    for (const f of PILLAR_FACTORS.reit[p]) {
      if (!seen.has(String(f.key))) { seen.add(String(f.key)); merged.push(f); }
    }
    (out as Record<string, unknown>)[p] = merged;
  }
  return out as ReturnType<typeof unionPillarMapping>;
}
